import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const PIN = process.env.REACT_APP_TV_PIN;
const REFRESH = 30000;

// ── Helpers ──────────────────────────────────────────────
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
const fmtSAR = (n) => `SAR ${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function statusColor(s = "") {
  const sl = s.toLowerCase();
  if (sl.includes("complet") || sl.includes("done") || sl.includes("deliver")) return "#00D4AA";
  if (sl.includes("delay") || sl.includes("overdue") || sl.includes("reject")) return "#FF4D6D";
  if (sl.includes("progress") || sl.includes("active") || sl.includes("order")) return "#4D9FFF";
  if (sl.includes("pending")) return "#FFB84D";
  return "#FFB84D";
}

// ── Achievement Sound (5 seconds) ────────────────────────
function playAchievementSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [
      { freq: 523.25, start: 0, dur: 0.15 },
      { freq: 659.25, start: 0.12, dur: 0.15 },
      { freq: 783.99, start: 0.24, dur: 0.15 },
      { freq: 1046.5, start: 0.36, dur: 0.3 },
      { freq: 783.99, start: 0.5, dur: 0.15 },
      { freq: 1046.5, start: 0.65, dur: 0.5 },
      { freq: 783.99, start: 1.2, dur: 0.15 },
      { freq: 1046.5, start: 1.4, dur: 0.15 },
      { freq: 1318.5, start: 1.6, dur: 0.8 },
      { freq: 1046.5, start: 2.5, dur: 0.15 },
      { freq: 1174.7, start: 2.7, dur: 0.15 },
      { freq: 1318.5, start: 2.9, dur: 1.0 },
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });
  } catch (e) { }
}
// function خارج الـ component
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else {
    document.exitFullscreen();
  }
};

// ── Animated Number ───────────────────────────────────────
function AnimatedNumber({ value, duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = Number(value) || 0;
    prev.current = end;
    if (start === end) { setDisplay(end); return; }
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{display}</>;
}

// ── Radial Gauge ──────────────────────────────────────────
function RadialGauge({ value = 0, size = 120, color = "#00D4AA", label, sublabel, thickness = 10 }) {
  const r = (size / 2) - thickness - 4;
  const circ = 2 * Math.PI * r;
  const arc = (value / 100) * circ * 0.75;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`g-${label}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="rgba(255,255,255,0.04)" strokeWidth={thickness}
          strokeDasharray={`${circ * 0.75} ${circ}`} strokeLinecap="round"
          transform={`rotate(135 ${size / 2} ${size / 2})`} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={`url(#g-${label})`} strokeWidth={thickness}
          strokeDasharray={`${arc} ${circ}`} strokeLinecap="round"
          transform={`rotate(135 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.34,1.56,0.64,1)", filter: `drop-shadow(0 0 6px ${color}60)` }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        {label && <span style={{ fontSize: size * 0.18, fontWeight: 700, color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: -0.5 }}>{label}</span>}
        {sublabel && <span style={{ fontSize: size * 0.08, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 1.5, marginTop: 1 }}>{sublabel}</span>}
      </div>
    </div>
  );
}

// ── Slim Bar ──────────────────────────────────────────────
function SlimBar({ value = 0, baseline, color = "#00D4AA", height = 4, glow = false }) {
  return (
    <div style={{ position: "relative", height, background: "rgba(255,255,255,0.05)", borderRadius: height }}>
      <div style={{
        height: "100%", width: `${Math.min(value, 100)}%`, background: color, borderRadius: height,
        transition: "width 1.2s cubic-bezier(0.34,1.56,0.64,1)",
        boxShadow: glow ? `0 0 8px ${color}80` : "none"
      }} />
      {baseline !== undefined && (
        <div style={{
          position: "absolute", top: -2, bottom: -2, left: `${Math.min(baseline, 100)}%`,
          width: 2, background: "rgba(255,255,255,0.3)", borderRadius: 1
        }} />
      )}
    </div>
  );
}

// ── Status Pill ───────────────────────────────────────────
function StatusPill({ label, color }) {
  return (
    <span style={{
      fontSize: 9, padding: "2px 8px", borderRadius: 3,
      background: `${color}18`, color, border: `0.5px solid ${color}40`,
      fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1.5,
      textTransform: "uppercase", fontWeight: 600
    }}>{label}</span>
  );
}

// ── Grid Background ───────────────────────────────────────
function GridBg() {
  return (
    <div style={{
      position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
      backgroundImage: `linear-gradient(rgba(0,212,170,0.03) 1px, transparent 1px),linear-gradient(90deg, rgba(0,212,170,0.03) 1px, transparent 1px)`,
      backgroundSize: "40px 40px"
    }} />
  );
}

// ── Corner Deco ───────────────────────────────────────────
function CornerDeco({ pos }) {
  const s = {
    position: "absolute", width: 16, height: 16,
    ...(pos === "tl" ? { top: 0, left: 0, borderTop: "1.5px solid #00D4AA", borderLeft: "1.5px solid #00D4AA" } : {}),
    ...(pos === "tr" ? { top: 0, right: 0, borderTop: "1.5px solid #00D4AA", borderRight: "1.5px solid #00D4AA" } : {}),
    ...(pos === "bl" ? { bottom: 0, left: 0, borderBottom: "1.5px solid #00D4AA", borderLeft: "1.5px solid #00D4AA" } : {}),
    ...(pos === "br" ? { bottom: 0, right: 0, borderBottom: "1.5px solid #00D4AA", borderRight: "1.5px solid #00D4AA" } : {}),
  };
  return <div style={s} />;
}

// ── Panel ─────────────────────────────────────────────────
function Panel({ title, badge, badgeColor = "#00D4AA", children, accent, noPad, onClick, style = {} }) {
  const ac = accent || "#00D4AA";
  return (
    <div onClick={onClick} style={{
      background: "rgba(10,18,35,0.85)", border: "0.5px solid rgba(255,255,255,0.06)",
      borderRadius: 8, backdropFilter: "blur(12px)", overflow: "hidden",
      position: "relative", boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
      cursor: onClick ? "pointer" : "default", transition: "border-color 0.2s", ...style
    }}
      onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = `${ac}40`)}
      onMouseLeave={e => onClick && (e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)")}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 1,
        background: `linear-gradient(90deg, transparent, ${ac}60, transparent)`
      }} />
      <CornerDeco pos="tl" /><CornerDeco pos="tr" />
      {title && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "9px 14px", borderBottom: "0.5px solid rgba(255,255,255,0.05)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 2, height: 12, background: ac, borderRadius: 1, boxShadow: `0 0 6px ${ac}` }} />
            <span style={{
              fontSize: 9, color: "rgba(255,255,255,0.4)", textTransform: "uppercase",
              letterSpacing: 2.5, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600
            }}>{title}</span>
          </div>
          {badge && <StatusPill label={badge} color={badgeColor} />}
        </div>
      )}
      <div style={noPad ? {} : { padding: "12px 14px", height: "100%", display: "flex", flexDirection: "column" }}>{children}</div>
    </div>
  );
}

// ── KPI Tile ──────────────────────────────────────────────
function KpiTile({ label, value, sub, color = "#00D4AA", alert, onClick, suffix = "" }) {
  return (
    <div onClick={onClick} style={{
      background: "rgba(10,18,35,0.85)",
      border: `0.5px solid ${alert ? `${color}40` : "rgba(255,255,255,0.06)"}`,
      borderRadius: 8, padding: "12px 14px", backdropFilter: "blur(12px)",
      position: "relative", overflow: "hidden", cursor: onClick ? "pointer" : "default",
      boxShadow: alert ? `0 0 20px ${color}20, 0 4px 24px rgba(0,0,0,0.4)` : "0 4px 24px rgba(0,0,0,0.4)",
      transition: "all 0.2s",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 1,
        background: `linear-gradient(90deg, transparent, ${color}50, transparent)`
      }} />
      <div style={{
        fontSize: 8, color: "rgba(255,255,255,0.3)", textTransform: "uppercase",
        letterSpacing: 2.5, marginBottom: 8, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600
      }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "'Barlow Condensed',sans-serif", lineHeight: 1 }}>
        <AnimatedNumber value={typeof value === "number" ? value : parseInt(value) || 0} />{suffix}
      </div>
      {sub && <div style={{
        fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 5,
        fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 0.5
      }}>{sub}</div>}
    </div>
  );
}

// ── Clock ─────────────────────────────────────────────────
function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const hh = String(t.getHours()).padStart(2, "0");
  const mm = String(t.getMinutes()).padStart(2, "0");
  const ss = String(t.getSeconds()).padStart(2, "0");
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", fontSize: 32, fontWeight: 300, letterSpacing: 3, lineHeight: 1 }}>
        {hh}<span style={{ opacity: t.getSeconds() % 2 === 0 ? 1 : 0.3, transition: "opacity 0.1s" }}>:</span>
        {mm}<span style={{ opacity: t.getSeconds() % 2 === 0 ? 1 : 0.3, transition: "opacity 0.1s" }}>:</span>{ss}
      </div>
      <div style={{ fontSize: 9, color: "rgba(0,212,170,0.7)", marginTop: 4, letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif" }}>
        {days[t.getDay()]} · {t.getDate()} {months[t.getMonth()]} {t.getFullYear()}
      </div>
    </div>
  );
}

// ── Pulse Dot ─────────────────────────────────────────────
function PulseDot({ color = "#00D4AA" }) {
  return (
    <div style={{ position: "relative", width: 8, height: 8 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color, opacity: 0.3, animation: "ripple 2s ease-out infinite" }} />
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }} />
    </div>
  );
}

// ── Task Completion Flash (5 seconds) ─────────────────────
function TaskCompletionFlash({ event, onDone }) {
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    if (!event) return;
    playAchievementSound();
    setTimeout(() => setOpacity(1), 50);
    setTimeout(() => setOpacity(0), 4500);
    setTimeout(() => onDone(), 5000);
  }, [event]);

  if (!event) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column",
      background: `rgba(0,212,170,0.12)`, backdropFilter: "blur(4px)",
      opacity, transition: "opacity 0.5s ease", pointerEvents: "none",
    }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(0,212,170,0.3) 0%, transparent 70%)" }} />
      <div style={{ position: "absolute", inset: 0, border: "3px solid rgba(0,212,170,0.6)", boxShadow: "inset 0 0 100px rgba(0,212,170,0.15), 0 0 100px rgba(0,212,170,0.3)" }} />

      {/* Scanline effect */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,212,170,0.03) 3px, rgba(0,212,170,0.03) 4px)" }} />

      <div style={{
        position: "relative", zIndex: 1, textAlign: "center",
        background: "rgba(10,18,35,0.95)", border: "1px solid rgba(0,212,170,0.5)",
        borderRadius: 20, padding: "48px 80px",
        boxShadow: "0 0 80px rgba(0,212,170,0.4), 0 0 200px rgba(0,212,170,0.1)"
      }}>
        <CornerDeco pos="tl" /><CornerDeco pos="tr" />
        <CornerDeco pos="bl" /><CornerDeco pos="br" />

        {/* Animated checkmark */}
        <div style={{
          width: 90, height: 90, borderRadius: "50%",
          background: "rgba(0,212,170,0.15)", border: "2px solid #00D4AA",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 24px", boxShadow: "0 0 40px rgba(0,212,170,0.5)"
        }}>
          <svg width="45" height="45" viewBox="0 0 45 45" fill="none">
            <path d="M9 22l11 11L36 13" stroke="#00D4AA" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 13, color: "rgba(0,212,170,0.7)", letterSpacing: 5, marginBottom: 12 }}>TASK COMPLETED</div>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 32, color: "#fff", fontWeight: 700, marginBottom: 10, maxWidth: 600, lineHeight: 1.2 }}>
          {event.sub_task || event.title || "Task"}
        </div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2, marginBottom: 20 }}>
          {event.project_id?.toUpperCase()} · {event.system}
        </div>

        {/* Progress bar countdown */}
        <div style={{ width: 300, height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, margin: "0 auto", overflow: "hidden" }}>
          <div style={{
            height: "100%", background: "#00D4AA", borderRadius: 2,
            animation: "countdown 5s linear forwards"
          }} />
        </div>
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────
function DetailModal({ title, onClose, children, wide = false }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24
    }}
      onClick={onClose}>
      <div style={{
        background: "rgba(10,18,35,0.98)", border: "0.5px solid rgba(0,212,170,0.3)",
        borderRadius: 12, padding: 28, width: "100%", maxWidth: wide ? 1100 : 800,
        maxHeight: "85vh", overflowY: "auto",
        boxShadow: "0 0 60px rgba(0,212,170,0.15)", position: "relative"
      }}
        onClick={e => e.stopPropagation()}>
        <CornerDeco pos="tl" /><CornerDeco pos="tr" />
        <CornerDeco pos="bl" /><CornerDeco pos="br" />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 16, color: "#fff", fontWeight: 700, letterSpacing: 2 }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(255,255,255,0.4)", padding: "4px 12px", cursor: "pointer", fontSize: 12 }}>✕ CLOSE</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Gantt Timeline ────────────────────────────────────────
function GanttChart({ projects }) {
  const now = new Date();
  const allDates = projects.flatMap(p => [p.start_date, p.end_date].filter(Boolean).map(d => new Date(d)));
  if (allDates.length === 0) return <div style={{ color: "rgba(255,255,255,0.2)", fontSize: 10, textAlign: "center", padding: 20 }}>NO TIMELINE DATA</div>;

  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));
  const totalDays = Math.max((maxDate - minDate) / 86400000, 1);
  const nowPct = Math.min(Math.max(((now - minDate) / 86400000) / totalDays * 100, 0), 100);

  const colors = ["#00D4AA", "#4D9FFF", "#FFB84D", "#FF4D6D", "#A78BFA", "#F472B6", "#34D399", "#60A5FA"];

  return (
    <div>
      {/* Timeline header */}
      <div style={{ display: "flex", marginBottom: 6, paddingLeft: 160 }}>
        {[0, 25, 50, 75, 100].map(p => (
          <div key={p} style={{ flex: 1, fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", textAlign: "center" }}>
            {new Date(minDate.getTime() + (totalDays * p / 100) * 86400000).toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
          </div>
        ))}
      </div>

      {/* Projects */}
      {projects.map((p, i) => {
        if (!p.start_date || !p.end_date) return null;
        const start = new Date(p.start_date);
        const end = new Date(p.end_date);
        const left = ((start - minDate) / 86400000) / totalDays * 100;
        const width = Math.max(((end - start) / 86400000) / totalDays * 100, 2);
        const c = colors[i % colors.length];
        const daysLeft = Math.ceil((end - now) / 86400000);
        const isOverdue = daysLeft < 0 && p.status !== 'Completed' && p.status !== 'Cancelled';

        return (
          <div key={p.id} style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 8 }}>
            <div style={{
              width: 150, fontSize: 9, color: "rgba(255,255,255,0.4)", fontFamily: "'Barlow Condensed',sans-serif",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0, textAlign: "right"
            }}>
              {p.short_name || (p.name || "").split(" ").slice(0, 3).join(" ")}
            </div>
            <div style={{ flex: 1, position: "relative", height: 24, background: "rgba(255,255,255,0.03)", borderRadius: 4 }}>
              {/* Today line */}
              <div style={{
                position: "absolute", top: 0, bottom: 0, left: `${nowPct}%`, width: 1.5,
                background: "rgba(255,255,255,0.4)", zIndex: 2, boxShadow: "0 0 4px rgba(255,255,255,0.3)"
              }} />
              {/* Bar */}
              <div style={{
                position: "absolute", top: 4, bottom: 4, left: `${left}%`, width: `${width}%`,
                background: `linear-gradient(90deg, ${c}80, ${c})`,
                borderRadius: 3, border: `0.5px solid ${c}`,
                boxShadow: `0 0 8px ${c}40`, minWidth: 4
              }}>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 6, overflow: "hidden" }}>
                  <span style={{
                    fontSize: 8, color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", whiteSpace: "nowrap",
                    textShadow: "0 1px 3px rgba(0,0,0,0.8)"
                  }}>
                    {p.status === 'Completed' ? "DONE" : isOverdue ? "OVERDUE" : `${daysLeft}D LEFT`}
                  </span>
                </div>
              </div>
            </div>
            <div style={{
              width: 60, fontSize: 9, color: isOverdue ? "#FF4D6D" : "rgba(255,255,255,0.3)",
              fontFamily: "'Barlow Condensed',sans-serif", textAlign: "right", flexShrink: 0
            }}>
              {new Date(p.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", "year": "2-digit" })}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, paddingLeft: 160 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 12, height: 1.5, background: "rgba(255,255,255,0.4)" }} />
          <span style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>TODAY</span>
        </div>
      </div>
    </div>
  );
}

// ── Live Activity Card ────────────────────────────────────
function LiveActivityFeed({ activities, projects }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (activities.length === 0) return;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentIdx(i => (i + 1) % activities.length);
        setFade(true);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, [activities.length]);

  if (activities.length === 0) return (
    <div style={{ textAlign: "center", padding: 20, color: "rgba(255,255,255,0.2)", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2 }}>NO RECENT ACTIVITY</div>
  );

  const activity = activities[currentIdx];
  const images = Array.isArray(activity.images) ? activity.images : (typeof activity.images === "string" ? JSON.parse(activity.images || "[]") : []);
  const project = projects.find(p => String(p.id) === String(activity.project_id));
  const timeAgo = activity.created_at ? Math.floor((Date.now() - new Date(activity.created_at)) / 60000) : null;

  return (
    <div style={{ opacity: fade ? 1 : 0, transition: "opacity 0.5s ease", display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Live badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <PulseDot color="#FF4D6D" />
        <span style={{ fontSize: 9, color: "#FF4D6D", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2, fontWeight: 700 }}>LIVE ACTIVITY</span>
        <span style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", marginLeft: "auto" }}>
          {currentIdx + 1}/{activities.length}
        </span>
      </div>

      {/* Activity content */}
      <div style={{ background: "rgba(255,77,109,0.05)", border: "0.5px solid rgba(255,77,109,0.2)", borderRadius: 8, padding: 20, flex: 1 }}>
        <div style={{ fontSize: 13, color: "#fff", fontWeight: 600, marginBottom: 6, fontFamily: "'Barlow Condensed',sans-serif", lineHeight: 1.3 }}>
          {activity.activity_name}
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>{activity.system}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginBottom: 3 }}>PROJECT</div>
            <div style={{ fontSize: 10, color: "#00D4AA", fontFamily: "'Barlow Condensed',sans-serif" }}>{project?.short_name || activity.project_id}</div>
          </div>
          <div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginBottom: 3 }}>WORKERS</div>
            <div style={{ fontSize: 10, color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{activity.workers_count || 0}</div>
          </div>
          <div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginBottom: 3 }}>REPORTED BY</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{activity.created_by}</div>
          </div>
          <div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginBottom: 3 }}>TIME</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", fontFamily: "'Barlow Condensed',sans-serif" }}>
              {timeAgo !== null ? (timeAgo < 60 ? `${timeAgo}m ago` : `${Math.floor(timeAgo / 60)}h ago`) : activity.time?.slice(0, 5)}
            </div>
          </div>
        </div>

        {/* Images */}
        {images.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {images.slice(0, 4).map((img, i) => (
              <img key={i} src={img} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6, border: "0.5px solid rgba(255,255,255,0.1)" }} />
            ))}
            {images.length > 4 && (
              <div style={{ width: 60, height: 60, borderRadius: 6, background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif" }}>+{images.length - 4}</div>
            )}
          </div>
        )}

        {activity.description && (
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 8, fontStyle: "italic", lineHeight: 1.5 }}>{activity.description}</div>
        )}
      </div>

      {/* Dots indicator */}
      <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 8 }}>
        {activities.slice(0, 8).map((_, i) => (
          <div key={i} style={{
            width: i === currentIdx ? 16 : 4, height: 4, borderRadius: 2,
            background: i === currentIdx ? "#FF4D6D" : "rgba(255,255,255,0.1)",
            transition: "all 0.3s"
          }} />
        ))}
      </div>
    </div>
  );
}

// ── Projects Map ──────────────────────────────────────────
function ProjectsMap({ projects }) {
  const validProjects = projects.filter(p => p.latitude && p.longitude);

  if (validProjects.length === 0) return (
    <div style={{ textAlign: "center", padding: 20, color: "rgba(255,255,255,0.2)", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2 }}>NO LOCATION DATA</div>
  );

  const center = [
    validProjects.reduce((s, p) => s + p.latitude, 0) / validProjects.length,
    validProjects.reduce((s, p) => s + p.longitude, 0) / validProjects.length,
  ];

  const colors = ["#00D4AA", "#4D9FFF", "#FFB84D", "#FF4D6D", "#A78BFA"];

  const createIcon = (color, label) => L.divIcon({
    html: `<div style="
      background:${color};
      width:32px;height:32px;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);border:2px solid white;
      box-shadow:0 0 10px ${color}80;
      display:flex;align-items:center;justify-content:center;
    "><div style="transform:rotate(45deg);color:white;font-size:10px;font-weight:700;font-family:'Barlow Condensed',sans-serif;">${label}</div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    className: "",
  });

  return (
    <div style={{ height: "100%", borderRadius: 8, overflow: "hidden", position: "relative" }}>
      <MapContainer center={center} zoom={5} style={{ height: "100%", width: "100%" }}
        zoomControl={false} attributionControl={false}>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
        {validProjects.map((p, i) => (
          <Marker key={p.id} position={[p.latitude, p.longitude]}
            icon={createIcon(colors[i % colors.length], String(i + 1))}>
            <Popup>
              <div style={{ fontFamily: "system-ui", fontSize: 12 }}>
                <strong>{p.name}</strong><br />
                {p.client}<br />
                {fmtSAR(p.total_value)}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {/* Legend overlay */}
      <div style={{
        position: "absolute", bottom: 8, left: 8, zIndex: 1000,
        background: "rgba(3,8,16,0.9)", border: "0.5px solid rgba(0,212,170,0.2)",
        borderRadius: 6, padding: "8px 10px"
      }}>
        {validProjects.map((p, i) => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: colors[i % colors.length] }} />
            <span style={{ fontSize: 8, color: "rgba(255,255,255,0.5)", fontFamily: "'Barlow Condensed',sans-serif" }}>
              {i + 1}. {(p.short_name || p.name || "").split(" ").slice(0, 2).join(" ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Budget Bar Chart ──────────────────────────────────────
function BudgetChart({ categories, expenses }) {
  if (!categories || categories.length === 0) return (
    <div style={{ textAlign: "center", padding: 20, color: "rgba(255,255,255,0.2)", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2 }}>NO BUDGET DATA</div>
  );
  const data = categories.map(cat => {
    const spent = expenses.filter(e => e.category_id === cat.category_id).reduce((s, e) => s + Number(e.amount || 0), 0);
    const allocated = Number(cat.allocated || 0);
    const pctUsed = allocated > 0 ? Math.round((spent / allocated) * 100) : 0;
    const color = pctUsed >= 90 ? "#FF4D6D" : pctUsed >= 70 ? "#FFB84D" : "#00D4AA";
    return { ...cat, spent, allocated, pctUsed, color };
  });
  return (
    <div>
      {data.map((d, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", fontFamily: "'Barlow Condensed',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>{d.category_name}</span>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 9, color: d.color, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{d.pctUsed}%</span>
              <span style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>
                {fmtSAR(d.spent)} / {fmtSAR(d.allocated)}
              </span>
            </div>
          </div>
          <SlimBar value={d.pctUsed} color={d.color} height={5} glow={d.pctUsed >= 90} />
        </div>
      ))}
    </div>
  );
}

// ── PIN Screen ────────────────────────────────────────────
function PinScreen({ onUnlock }) {
  const [val, setVal] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const submit = () => {
    if (val === PIN) { onUnlock(); }
    else { setError(true); setShake(true); setVal(""); setTimeout(() => setShake(false), 600); }
  };
  return (
    <div style={{ minHeight: "100vh", background: "#030810", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
      <GridBg />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,212,170,0.08) 0%, transparent 70%)", top: "20%", left: "30%" }} />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", marginBottom: 40 }}>
        <div style={{ width: 80, height: 80, margin: "0 auto 20px", position: "relative" }}>
          <div style={{ position: "absolute", inset: -8, borderRadius: "50%", border: "1px solid rgba(0,212,170,0.2)", animation: "spin 8s linear infinite" }} />
          <div style={{ width: 80, height: 80, background: "rgba(0,212,170,0.1)", border: "1px solid rgba(0,212,170,0.4)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="#00D4AA" strokeWidth="1.5" />
              <path d="M7 11V7a5 5 0 0110 0v4" stroke="#00D4AA" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        </div>
        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", color: "#fff", fontSize: 28, fontWeight: 700, letterSpacing: 8 }}>RITIS</div>
        <div style={{ color: "rgba(0,212,170,0.7)", fontSize: 10, letterSpacing: 4, marginTop: 6, fontFamily: "'Barlow Condensed',sans-serif" }}>COMMAND & CONTROL CENTER</div>
        <div style={{ width: 60, height: 1, background: "linear-gradient(90deg, transparent, #00D4AA, transparent)", margin: "12px auto 0" }} />
      </div>
      <div style={{ position: "relative", zIndex: 1, background: "rgba(10,18,35,0.9)", border: "0.5px solid rgba(0,212,170,0.2)", borderRadius: 12, padding: "32px 36px", width: 340, backdropFilter: "blur(20px)", boxShadow: "0 0 40px rgba(0,212,170,0.1)", animation: shake ? "shake 0.5s ease" : "fadeUp 0.5s ease" }}>
        <CornerDeco pos="tl" /><CornerDeco pos="tr" /><CornerDeco pos="bl" /><CornerDeco pos="br" />
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textAlign: "center", marginBottom: 18, letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif" }}>ENTER ACCESS PIN</div>
        <input type="password" value={val}
          onChange={e => { setVal(e.target.value); setError(false); }}
          onKeyDown={e => e.key === "Enter" && submit()}
          placeholder="· · · · · · · ·" autoFocus
          style={{ width: "100%", background: "rgba(255,255,255,0.03)", border: error ? "1px solid #FF4D6D" : "0.5px solid rgba(255,255,255,0.1)", borderRadius: 6, padding: "14px 16px", color: "#fff", fontSize: 20, letterSpacing: 8, outline: "none", textAlign: "center", boxSizing: "border-box", fontFamily: "monospace" }} />
        {error && <div style={{ color: "#FF4D6D", fontSize: 9, textAlign: "center", marginTop: 10, letterSpacing: 1, fontFamily: "'Barlow Condensed',sans-serif" }}>INCORRECT PIN — TRY AGAIN</div>}
        <button onClick={submit} style={{ width: "100%", marginTop: 16, background: "linear-gradient(135deg, rgba(0,212,170,0.2), rgba(0,212,170,0.1))", border: "0.5px solid rgba(0,212,170,0.4)", borderRadius: 6, padding: "13px", color: "#00D4AA", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif" }}>UNLOCK ACCESS</button>
      </div>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}} @keyframes spin{to{transform:rotate(360deg)}} @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}} @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;400;500;600;700&family=Barlow:wght@400;500&display=swap');`}</style>
    </div>
  );
}


// ── Project Spotlight (Screensaver) ───────────────────────
function ProjectSpotlight({ projects, tasks, phases, milestones, budgetCats, budgetExp, activities, teamMembers, onExit }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [fade, setFade] = useState(true);
  const DURATION = 30000;

  useEffect(() => {
    setProgress(0);
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / DURATION) * 100, 100));
    }, 100);

    const timer = setTimeout(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentIdx(i => (i + 1) % projects.length);
        setFade(true);
      }, 600);
    }, DURATION);

    return () => { clearInterval(progressInterval); clearTimeout(timer); };
  }, [currentIdx, projects.length]);

  const p = projects[currentIdx];
  if (!p) return null;

  const projTasks = tasks.filter(t => String(t.project_id) === String(p.id));
  const doneTasks = projTasks.filter(t => t.status === "Completed").length;
  const activeTasks = projTasks.filter(t => t.status === "In Progress").length;
  const prog = projTasks.length > 0 ? Math.round(projTasks.reduce((s, t) => s + (t.progress || 0), 0) / projTasks.length) : 0;

  const projMilestones = milestones.filter(m => String(m.project_id) === String(p.id));
  const doneMilestones = projMilestones.filter(m => m.status === "Completed").length;

  const projBudgetCats = budgetCats.filter(b => String(b.project_id) === String(p.id));
  const projBudgetExp = budgetExp.filter(e => String(e.project_id) === String(p.id));
  const totalAllocated = projBudgetCats.reduce((s, c) => s + Number(c.allocated || 0), 0);
  const totalSpent = projBudgetExp.reduce((s, e) => s + Number(e.amount || 0), 0);
  const budgetPct = totalAllocated > 0 ? Math.round((totalSpent / totalAllocated) * 100) : 0;

  const projActivities = activities.filter(a => String(a.project_id) === String(p.id));
  const latestActivity = projActivities[0];
  const latestImages = latestActivity ? (Array.isArray(latestActivity.images) ? latestActivity.images : JSON.parse(latestActivity.images || "[]")) : [];

  const projPhases = phases.filter(ph => String(ph.project_id) === String(p.id));
  const daysLeft = p.end_date ? Math.ceil((new Date(p.end_date) - new Date()) / 86400000) : null;
  const isOverdue = daysLeft !== null && daysLeft < 0;

  const colors = ["#00D4AA", "#4D9FFF", "#FFB84D", "#FF4D6D", "#A78BFA"];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "#030810",
      opacity: fade ? 1 : 0,
      transition: "opacity 0.6s ease",
    }}>
      <GridBg />

      {/* Glow */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(0,212,170,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />

      {/* Exit hint */}
      <div style={{ position: "absolute", top: 20, right: 20, zIndex: 10, fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2 }}>
        MOVE MOUSE TO EXIT
      </div>

      {/* Project counter */}
      <div style={{ position: "absolute", top: 20, left: 20, zIndex: 10, display: "flex", gap: 6 }}>
        {projects.map((_, i) => (
          <div key={i} style={{ width: i === currentIdx ? 20 : 6, height: 4, borderRadius: 2, background: i === currentIdx ? "#00D4AA" : "rgba(255,255,255,0.1)", transition: "all 0.3s" }} />
        ))}
      </div>

      {/* Main Content */}
      <div style={{ position: "relative", zIndex: 1, height: "100vh", display: "flex", flexDirection: "column", padding: "60px 60px 40px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 40 }}>
          <div>
            <div style={{ fontSize: 11, color: "rgba(0,212,170,0.6)", letterSpacing: 4, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 8 }}>
              PROJECT {currentIdx + 1} OF {projects.length}
            </div>
            <h1 style={{ fontSize: 42, fontWeight: 700, color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, lineHeight: 1.1, maxWidth: 800, margin: 0 }}>
              {p.name}
            </h1>
            {p.client && (
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 8, letterSpacing: 1 }}>
                {p.client}
              </div>
            )}
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{
              fontSize: 48, fontWeight: 700,
              color: p.status === 'Completed' ? "#00D4AA" : isOverdue ? "#FF4D6D" : "#00D4AA",
              fontFamily: "'Barlow Condensed',sans-serif", lineHeight: 1
            }}>
              {p.status === 'Completed' ? "DONE" : isOverdue ? "OVERDUE" : daysLeft !== null ? `${daysLeft}D` : "—"}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2, marginTop: 4 }}>
              {p.status === 'Completed' ? "COMPLETED" : isOverdue ? "PAST DEADLINE" : "DAYS LEFT"}
            </div>
            <div style={{ fontSize: 14, color: "#00D4AA", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 8, fontWeight: 700 }}>
              {fmtSAR(p.total_value)}
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, flex: 1 }}>

          {/* Progress */}
          <div style={{ background: "rgba(10,18,35,0.85)", border: "0.5px solid rgba(0,212,170,0.2)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, rgba(0,212,170,0.6), transparent)" }} />
            <RadialGauge size={140} value={prog} color={prog >= 75 ? "#00D4AA" : prog >= 50 ? "#FFB84D" : "#4D9FFF"} label={`${prog}%`} sublabel="progress" thickness={12} />
            <div style={{ marginTop: 20, width: "100%" }}>
              {[
                { l: "Completed", v: doneTasks, c: "#00D4AA" },
                { l: "In Progress", v: activeTasks, c: "#4D9FFF" },
                { l: "Total", v: projTasks.length, c: "rgba(255,255,255,0.3)" },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontFamily: "'Barlow Condensed',sans-serif" }}>{s.l}</span>
                  <span style={{ fontSize: 14, color: s.c, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{s.v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Phases */}
          <div style={{ background: "rgba(10,18,35,0.85)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 24, overflowY: "auto" }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 16 }}>PHASES</div>
            {projPhases.length === 0
              ? <div style={{ color: "rgba(255,255,255,0.2)", fontSize: 12, textAlign: "center", marginTop: 40 }}>No phases</div>
              : projPhases.map((ph, i) => {
                const phTasks = tasks.filter(t => String(t.project_id) === String(p.id) && t.system === ph.system_name);
                const phProg = phTasks.length > 0 ? Math.round(phTasks.reduce((s, t) => s + (t.progress || 0), 0) / phTasks.length) : 0;
                const c = phProg >= 100 ? "#00D4AA" : ph.is_critical ? "#FF4D6D" : "#4D9FFF";
                return (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: "'Barlow Condensed',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%" }}>{ph.system_name || ph.name}</span>
                      <span style={{ fontSize: 12, color: c, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{phProg}%</span>
                    </div>
                    <SlimBar value={phProg} color={c} height={6} glow={phProg >= 100} />
                  </div>
                );
              })}
          </div>

          {/* Milestones + Budget */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Milestones */}
            <div style={{ background: "rgba(10,18,35,0.85)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 20, flex: 1, overflowY: "auto" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 12 }}>
                MILESTONES · {doneMilestones}/{projMilestones.length}
              </div>
              {projMilestones.slice(0, 6).map((m, i) => {
                const isDone = m.status === "Completed";
                const ml = m.planned_date ? Math.ceil((new Date(m.planned_date) - new Date()) / 86400000) : null;
                const isOv = !isDone && ml !== null && ml <= 0;
                const c = isDone ? "#00D4AA" : isOv ? "#FF4D6D" : ml !== null && ml <= 7 ? "#FFB84D" : "#4D9FFF";
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", border: `1.5px solid ${c}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {isDone && <div style={{ width: 4, height: 4, borderRadius: "50%", background: c }} />}
                    </div>
                    <span style={{ fontSize: 10, color: isDone ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.6)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: isDone ? "line-through" : "none" }}>{m.title || m.name}</span>
                    <span style={{ fontSize: 9, color: c, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, flexShrink: 0 }}>
                      {isDone ? "✓" : isOv ? "OVR" : ml !== null ? `${ml}D` : "—"}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Budget */}
            <div style={{ background: "rgba(10,18,35,0.85)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 12 }}>BUDGET</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: budgetPct >= 90 ? "#FF4D6D" : "#FFB84D", fontFamily: "'Barlow Condensed',sans-serif" }}>{fmtSAR(totalSpent)}</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif", alignSelf: "flex-end" }}>/ {fmtSAR(totalAllocated)}</span>
              </div>
              <SlimBar value={budgetPct} color={budgetPct >= 90 ? "#FF4D6D" : budgetPct >= 70 ? "#FFB84D" : "#00D4AA"} height={8} glow={budgetPct >= 90} />
              <div style={{ fontSize: 11, color: budgetPct >= 90 ? "#FF4D6D" : "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 6, textAlign: "right" }}>{budgetPct}% USED</div>
            </div>
          </div>

          {/* Live Activity */}
          <div style={{ background: "rgba(10,18,35,0.85)", border: "0.5px solid rgba(255,77,109,0.2)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <PulseDot color="#FF4D6D" />
              <span style={{ fontSize: 10, color: "#FF4D6D", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 3, fontWeight: 700 }}>LATEST ACTIVITY</span>
            </div>
            {latestActivity ? (
              <>
                {latestImages.length > 0 && (
                  <img src={latestImages[0]} alt="" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 8, marginBottom: 14, border: "0.5px solid rgba(255,255,255,0.1)" }} />
                )}
                <div style={{ fontSize: 16, color: "#fff", fontWeight: 600, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 8, lineHeight: 1.3 }}>{latestActivity.activity_name}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 12 }}>{latestActivity.system}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: "auto" }}>
                  {[
                    { l: "Workers", v: latestActivity.workers_count || 0, c: "#FFB84D" },
                    { l: "By", v: latestActivity.created_by, c: "rgba(255,255,255,0.5)" },
                  ].map((s, i) => (
                    <div key={i}>
                      <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginBottom: 3 }}>{s.l}</div>
                      <div style={{ fontSize: 12, color: s.c, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600 }}>{s.v}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", color: "rgba(255,255,255,0.2)", fontSize: 12, marginTop: 40, fontFamily: "'Barlow Condensed',sans-serif" }}>NO RECENT ACTIVITY</div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Progress Bar */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "rgba(255,255,255,0.05)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "#00D4AA", transition: "width 0.1s linear", boxShadow: "0 0 8px #00D4AA" }} />
      </div>
    </div>
  );
}
// ── Main Dashboard ────────────────────────────────────────
function CommandCenter() {
  const [raw, setRaw] = useState({ projects: [], tasks: [], phases: [], materials: [], milestones: [], challenges: [], team: [], teamMembers: [], budgetCats: [], budgetExp: [], activities: [], tickets: [] });
  const [selectedProject, setSelectedProject] = useState("all");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [flashEvent, setFlashEvent] = useState(null);
  const [spotlightMode, setSpotlightMode] = useState(false);
  const idleTimerRef = useRef(null);
  const IDLE_TIME = 60000; // 60 ثانية

  const fetchAll = useCallback(async () => {
    const [
      { data: projects },
      { data: tasks },
      { data: phases },
      { data: materials },
      { data: milestones },
      { data: challenges },
      { data: team },
      { data: teamMembers },
      { data: budgetCats },
      { data: budgetExp },
      { data: activities },
      { data: tickets },
    ] = await Promise.all([
      supabase.from("projects").select("*"),
      supabase.from("tasks").select("*"),
      supabase.from("project_phases").select("*").order("baseline_start"),
      supabase.from("materials").select("*"),
      supabase.from("project_milestones").select("*").order("planned_date"),
      supabase.from("task_challenges").select("*").order("created_at", { ascending: false }),
      supabase.from("user_profiles").select("*").eq("is_active", true),
      supabase.from("team_members").select("*").eq("status", "Active"),
      supabase.from("budget_categories").select("*"),
      supabase.from("budget_expenses").select("*"),
      supabase.from("daily_activities").select("*").order("created_at", { ascending: false }).limit(20),
      // itsm_tickets is now the canonical ticket table (legacy `tickets` is
      // retired as of the ITSM rebuild and stays permanently empty).
      supabase.from("itsm_tickets").select("id, status"),
    ]);
    setRaw({
      projects: projects || [], tasks: tasks || [], phases: phases || [], materials: materials || [],
      milestones: milestones || [], challenges: challenges || [], team: team || [],
      teamMembers: teamMembers || [], budgetCats: budgetCats || [], budgetExp: budgetExp || [],
      activities: activities || [], tickets: tickets || [],
    });
    setLastUpdate(new Date());
    setLoading(false);
  }, []);
  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, REFRESH);
    const channel = supabase
      .channel("tasks-realtime")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "tasks" }, (payload) => {
        if (payload.new.status === "Completed" && payload.old.status !== "Completed") {
          setFlashEvent(payload.new);
          fetchAll();
        }
      })
      .subscribe();
    return () => { clearInterval(interval); supabase.removeChannel(channel); };
  }, [fetchAll]);

  // ── Computed ────────────────────────────────────────────


  // Idle detection
  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        setSpotlightMode(true);
      }, IDLE_TIME);
    };

    const handleActivity = () => {
      setSpotlightMode(false);
      resetTimer();
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, handleActivity));

    // ابدأ العداد
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      clearTimeout(idleTimerRef.current);
    };
  }, []); // ← array فاضية
  const projId = selectedProject === "all" ? null : selectedProject;
  const projects = raw.projects;
  const tasks = projId ? raw.tasks.filter(t => String(t.project_id) === String(projId)) : raw.tasks;
  const phases = projId ? raw.phases.filter(p => String(p.project_id) === String(projId)) : raw.phases;
  const materials = projId ? raw.materials.filter(m => String(m.project_id) === String(projId)) : raw.materials;
  const milestones = projId ? raw.milestones.filter(m => String(m.project_id) === String(projId)) : raw.milestones;
  const challenges = projId ? raw.challenges.filter(c => String(c.project_id) === String(projId)) : raw.challenges;
  const budgetCats = projId ? raw.budgetCats.filter(b => String(b.project_id) === String(projId)) : raw.budgetCats;
  const budgetExp = projId ? raw.budgetExp.filter(e => String(e.project_id) === String(projId)) : raw.budgetExp;
  const activities = projId ? raw.activities.filter(a => String(a.project_id) === String(projId)) : raw.activities;

  const doneTasks = tasks.filter(t => t.status === "Completed").length;
  const delayedTasks = tasks.filter(t => ["Delayed", "Overdue"].includes(t.status)).length;
  const inProgressTasks = tasks.filter(t => t.status === "In Progress").length;
  const notStarted = Math.max(tasks.length - doneTasks - delayedTasks - inProgressTasks, 0);

  const deliveredMats = materials.filter(m => m.delivery_status === "Delivered").length;
  const orderedMats = materials.filter(m => m.delivery_status === "Ordered").length;
  const pendingMats = materials.filter(m => m.delivery_status === "Pending").length;

  const doneMilestones = milestones.filter(m => m.status === "Completed").length;
  const inProgressMiles = milestones.filter(m => m.status === "In Progress").length;
  const overdueMilestones = milestones.filter(m => m.status !== "Completed" && m.planned_date && new Date(m.planned_date) < new Date()).length;


  const getProjectProgress = (pid) => {
    const p = raw.projects.find(proj => String(proj.id) === String(pid));

    // لو الـ status Completed أو Cancelled رجع 100 أو 0
    if (p?.status === 'Completed') return 100;
    if (p?.status === 'Cancelled') return 0;
    if (p?.status === 'On Hold') {
      // احسب من الـ tasks بس خليه ثابت
      const pt = raw.tasks.filter(t => String(t.project_id) === String(pid));
      return pt.length === 0 ? 0 : Math.round(pt.reduce((s, t) => s + (t.progress || 0), 0) / pt.length);
    }

    // Active — احسب من الـ tasks
    const pt = raw.tasks.filter(t => String(t.project_id) === String(pid));
    if (pt.length === 0) return 0;

    const avg = Math.round(pt.reduce((s, t) => s + (t.progress || 0), 0) / pt.length);

    // لو كل الـ tasks completed تلقائي يبقى 100
    const allCompleted = pt.length > 0 && pt.every(t => t.status === 'Completed');
    return allCompleted ? 100 : avg;
  };
  const getPhaseProgress = (ph) => {
    const phaseTasks = raw.tasks.filter(t =>
      String(t.project_id) === String(ph.project_id) &&
      (t.system === ph.system_name ||
        t.system === ph.name ||
        t.system === ph.phase_name)
    );
    if (phaseTasks.length === 0) return ph.progress || 0;
    return Math.round(
      phaseTasks.reduce((s, t) => s + (t.progress || 0), 0) / phaseTasks.length
    );
  };

  const overallProgress = projects.length > 0 ? Math.round(projects.reduce((s, p) => s + getProjectProgress(p.id), 0) / projects.length) : 0;

  const totalAllocated = budgetCats.reduce((s, c) => s + Number(c.allocated || 0), 0);
  const totalSpent = budgetExp.reduce((s, e) => s + Number(e.amount || 0), 0);
  const budgetPct = pct(totalSpent, totalAllocated);

  const totalContractValue = projects.reduce((s, p) => s + Number(p.total_value || 0), 0);
  const totalWorkers = raw.teamMembers.length;
  const totalWorkersByProject = (pid) => raw.teamMembers.filter(m => {
    const assignments = raw.tasks.filter(t => String(t.project_id) === String(pid) && t.assigned_to === m.name);
    return assignments.length > 0;
  }).length;

  const getProjectName = (id) => {
    const p = projects.find(p => String(p.id) === String(id));
    return p ? (p.short_name || p.name || "").split(" ").slice(0, 2).join(" ") : "—";
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#030810", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <GridBg />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        <div style={{ width: 48, height: 48, border: "1px solid rgba(0,212,170,0.2)", borderTop: "1px solid #00D4AA", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 20px" }} />
        <div style={{ color: "rgba(0,212,170,0.7)", fontSize: 10, letterSpacing: 4, fontFamily: "'Barlow Condensed',sans-serif" }}>CONNECTING TO RITIS NETWORK...</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;400;500;600;700&display=swap');`}</style>
    </div>
  );

  return (
    <div style={{ background: "#030810", minHeight: "100vh", padding: "10px 14px 14px", color: "#e2e8f0", fontFamily: "'Barlow',system-ui,sans-serif", fontSize: 12, position: "relative" }}>
      <GridBg />
      {/* Spotlight Mode */}
      {spotlightMode && projects.length > 0 && (
        <ProjectSpotlight
          projects={projects}
          tasks={raw.tasks}
          phases={raw.phases}
          milestones={raw.milestones}
          budgetCats={raw.budgetCats}
          budgetExp={raw.budgetExp}
          activities={raw.activities}
          teamMembers={raw.teamMembers}
          onExit={() => setSpotlightMode(false)}
        />
      )}

      {/* Flash */}
      {flashEvent && <TaskCompletionFlash event={flashEvent} onDone={() => setFlashEvent(null)} />}

      {/* Modal */}
      {modal && (
        <DetailModal title={modal.title} onClose={() => setModal(null)} wide={modal.wide}>
          {modal.type === "tasks" && (
            <div>
              {[
                { l: "Completed", items: tasks.filter(t => t.status === "Completed"), c: "#00D4AA" },
                { l: "In Progress", items: tasks.filter(t => t.status === "In Progress"), c: "#4D9FFF" },
                { l: "Delayed", items: tasks.filter(t => ["Delayed", "Overdue"].includes(t.status)), c: "#FF4D6D" },
                { l: "Not Started", items: tasks.filter(t => !["Completed", "In Progress", "Delayed", "Overdue"].includes(t.status)), c: "rgba(255,255,255,0.3)" },
              ].map(g => g.items.length > 0 && (
                <div key={g.l} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 9, color: g.c, letterSpacing: 2, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 8, fontWeight: 700 }}>{g.l} ({g.items.length})</div>
                  {g.items.map((t, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "rgba(255,255,255,0.02)", borderRadius: 4, marginBottom: 4, borderLeft: `2px solid ${g.c}` }}>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>{t.sub_task}</span>
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif" }}>{t.system} · {getProjectName(t.project_id)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {modal.type === "contract" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
                {[
                  { l: "Total Portfolio Value", v: fmtSAR(totalContractValue), c: "#00D4AA" },
                  { l: "Active Projects", v: projects.filter(p => p.status === "Active").length, c: "#4D9FFF" },
                  { l: "Total Workforce", v: totalWorkers, c: "#FFB84D" },
                ].map((s, i) => (
                  <div key={i} style={{ background: `${s.c}10`, border: `0.5px solid ${s.c}30`, borderRadius: 8, padding: "16px", textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: s.c, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 6 }}>{s.v}</div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }}>
                {projects.map((p, i) => {
                  const prog = getProjectProgress(p.id);
                  const daysLeft = p.end_date ? Math.ceil((new Date(p.end_date) - new Date()) / 86400000) : null;
                  return (
                    <div key={p.id} style={{ background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "14px" }}>
                      <div style={{ fontSize: 12, color: "#fff", fontWeight: 600, marginBottom: 8, fontFamily: "'Barlow Condensed',sans-serif" }}>{p.name}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                        {[
                          { l: "Contract Value", v: fmtSAR(p.total_value), c: "#00D4AA" },
                          { l: "Client", v: p.client || "—", c: "rgba(255,255,255,0.5)" },
                          { l: "Start", v: p.start_date ? new Date(p.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—", c: "rgba(255,255,255,0.4)" },
                          { l: "End", v: p.end_date ? new Date(p.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—", c: daysLeft && daysLeft < 0 && p.status !== 'Completed' ? "#FF4D6D" : "rgba(255,255,255,0.4)" },
                        ].map((s, j) => (
                          <div key={j}>
                            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginBottom: 2 }}>{s.l}</div>
                            <div style={{ fontSize: 11, color: s.c, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600 }}>{s.v}</div>
                          </div>
                        ))}
                      </div>
                      <SlimBar value={prog} color={prog >= 75 ? "#00D4AA" : prog >= 50 ? "#FFB84D" : "#4D9FFF"} height={4} glow />
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                        <span style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>{prog}% complete</span>
                        {daysLeft !== null && (
                          <span style={{
                            fontSize: 8,
                            color: p.status === 'Completed' ? "#00D4AA" : daysLeft < 0 ? "#FF4D6D" : "rgba(255,255,255,0.2)",
                            fontFamily: "'Barlow Condensed',sans-serif"
                          }}>
                            {p.status === 'Completed' ? "DONE" : daysLeft < 0 ? "OVERDUE" : `${daysLeft}D LEFT`}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {modal.type === "materials" && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{["Item", "System", "Project", "Qty", "Unit", "Status", "Supplier"].map(h => (
                  <th key={h} style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", textAlign: "left", padding: "6px 8px", borderBottom: "0.5px solid rgba(255,255,255,0.06)", textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {materials.map((m, i) => {
                  const c = statusColor(m.delivery_status);
                  return (
                    <tr key={i} style={{ borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                      <td style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", padding: "7px 8px" }}>{m.item_name || m.name || "—"}</td>
                      <td style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", padding: "7px 8px" }}>{m.system || "—"}</td>
                      <td style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", padding: "7px 8px" }}>{getProjectName(m.project_id)}</td>
                      <td style={{ fontSize: 10, color: "#fff", padding: "7px 8px", fontFamily: "'Barlow Condensed',sans-serif" }}>{m.quantity || "—"}</td>
                      <td style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", padding: "7px 8px" }}>{m.unit || "—"}</td>
                      <td style={{ padding: "7px 8px" }}><StatusPill label={m.delivery_status || "—"} color={c} /></td>
                      <td style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", padding: "7px 8px" }}>{m.supplier || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {modal.type === "milestones" && (
            <div>
              {milestones.map((m, i) => {
                const isDone = m.status === "Completed";
                const daysLeft = m.planned_date ? Math.ceil((new Date(m.planned_date) - new Date()) / 86400000) : null;
                const isOverdue = !isDone && daysLeft !== null && daysLeft <= 0;
                const c = isDone ? "#00D4AA" : isOverdue ? "#FF4D6D" : daysLeft !== null && daysLeft <= 7 ? "#FFB84D" : "#4D9FFF";
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${c}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isDone && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#00D4AA" }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: isDone ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.6)", textDecoration: isDone ? "line-through" : "none" }}>{m.title || m.milestone_name || m.name}</div>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 2 }}>
                        {getProjectName(m.project_id)} · {m.planned_date ? new Date(m.planned_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </div>
                    </div>
                    <StatusPill label={m.status || "—"} color={c} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: c, fontFamily: "'Barlow Condensed',sans-serif", minWidth: 60, textAlign: "right" }}>
                      {isDone ? "DONE" : daysLeft !== null ? (daysLeft <= 0 ? "OVERDUE" : `${daysLeft}D LEFT`) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {modal.type === "budget" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
                {[
                  { l: "Total Allocated", v: fmtSAR(totalAllocated), c: "#4D9FFF" },
                  { l: "Total Spent", v: fmtSAR(totalSpent), c: budgetPct >= 90 ? "#FF4D6D" : "#FFB84D" },
                  { l: "Remaining", v: fmtSAR(totalAllocated - totalSpent), c: "#00D4AA" },
                ].map((s, i) => (
                  <div key={i} style={{ background: `${s.c}10`, border: `0.5px solid ${s.c}30`, borderRadius: 8, padding: "12px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: s.c, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.v}</div>
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, marginTop: 4 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <BudgetChart categories={budgetCats} expenses={budgetExp} />
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 2, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 8 }}>RECENT EXPENSES</div>
                {budgetExp.slice(0, 10).map((e, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "rgba(255,255,255,0.02)", borderRadius: 4, marginBottom: 4 }}>
                    <div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{e.description || "—"}</div>
                      <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>{e.recipient} · {e.expense_date}</div>
                    </div>
                    <span style={{ fontSize: 12, color: "#FFB84D", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{fmtSAR(e.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {modal.type === "team" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
              {raw.teamMembers.map((member, i) => {
                const initials = (member.name || "??").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                const colors = ["#00D4AA", "#4D9FFF", "#FFB84D", "#FF4D6D", "#A78BFA"];
                const c = colors[i % 5];
                const memberTasks = raw.tasks.filter(t => t.assigned_to === member.name);
                const done = memberTasks.filter(t => t.status === "Completed").length;
                const active = memberTasks.filter(t => t.status === "In Progress").length;
                return (
                  <div key={i} style={{ background: `${c}08`, border: `0.5px solid ${c}20`, borderRadius: 8, padding: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      {member.avatar
                        ? <img src={member.avatar} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: `1px solid ${c}50` }} />
                        : <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${c}20`, border: `1px solid ${c}50`, color: c, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif" }}>{initials}</div>
                      }
                      <div>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>{member.name}</div>
                        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif" }}>{member.position || "—"}</div>
                        <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>{member.nationality || ""} · {member.department || ""}</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                      {[
                        { l: "Tasks", v: memberTasks.length, c: "rgba(255,255,255,0.3)" },
                        { l: "Done", v: done, c: "#00D4AA" },
                        { l: "Active", v: active, c: "#4D9FFF" },
                      ].map((s, j) => (
                        <div key={j} style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: s.c, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.v}</div>
                          <div style={{ fontSize: 7, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>{s.l}</div>
                        </div>
                      ))}
                    </div>
                    <SlimBar value={pct(done, memberTasks.length)} color={c} height={3} />
                    {member.phone && <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", marginTop: 6, fontFamily: "'Barlow Condensed',sans-serif" }}>{member.phone}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </DetailModal>
      )}

      {/* ══ HEADER ════════════════════════════════════════ */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 10, borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", inset: -6, borderRadius: "50%", border: "0.5px solid rgba(0,212,170,0.2)", animation: "spin 10s linear infinite" }} />
            <div style={{ width: 44, height: 44, background: "rgba(0,212,170,0.08)", border: "0.5px solid rgba(0,212,170,0.3)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="22" height="15" viewBox="0 0 140 95" fill="none">
                <rect x="2" y="18" width="55" height="55" rx="4" fill="rgba(0,212,170,0.9)" transform="rotate(45 29 45)" />
                <rect x="2" y="18" width="48" height="48" rx="3" fill="rgba(0,212,170,0.4)" transform="rotate(45 85 42)" />
                <polygon points="29,18 49,38 29,58 9,38" fill="#030810" />
                <polygon points="85,16 103,34 85,52 67,34" fill="#030810" opacity="0.8" />
              </svg>
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 6, color: "#fff", lineHeight: 1 }}>RITIS</div>
            <div style={{ fontSize: 8, color: "rgba(0,212,170,0.6)", letterSpacing: 3, textTransform: "uppercase", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 2 }}>Command & Control Center</div>
          </div>
          <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.07)", margin: "0 8px" }} />
          <div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", letterSpacing: 2, marginBottom: 3, fontFamily: "'Barlow Condensed',sans-serif" }}>ACTIVE PROJECTS</div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxWidth: 600 }}>
              <button onClick={() => setSelectedProject("all")} style={{ fontSize: 9, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1.5, fontWeight: 600, border: "0.5px solid", transition: "all 0.2s", borderColor: selectedProject === "all" ? "#00D4AA" : "rgba(255,255,255,0.08)", background: selectedProject === "all" ? "rgba(0,212,170,0.1)" : "transparent", color: selectedProject === "all" ? "#00D4AA" : "rgba(255,255,255,0.25)" }}>ALL</button>
              {projects.map(p => (
                <button key={p.id} onClick={() => setSelectedProject(selectedProject === String(p.id) ? "all" : String(p.id))} style={{ fontSize: 9, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1, fontWeight: 600, border: "0.5px solid", transition: "all 0.2s", borderColor: selectedProject === String(p.id) ? "#00D4AA" : "rgba(255,255,255,0.08)", background: selectedProject === String(p.id) ? "rgba(0,212,170,0.1)" : "transparent", color: selectedProject === String(p.id) ? "#00D4AA" : "rgba(255,255,255,0.25)" }}>
                  {(p.short_name || p.name || "").split(" ").slice(0, 2).join(" ").toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", marginBottom: 4 }}>
              <PulseDot />
              <span style={{ fontSize: 9, color: "rgba(0,212,170,0.7)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1.5 }}>LIVE · REALTIME</span>
            </div>
            {lastUpdate && <div style={{ fontSize: 8, color: "rgba(255,255,255,0.15)", fontFamily: "'Barlow Condensed',sans-serif" }}>LAST SYNC {lastUpdate.toTimeString().slice(0, 8)}</div>}
          </div>
          <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.07)" }} />
          <Clock />
        </div>
        <button
          onClick={toggleFullscreen}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "0.5px solid rgba(255,255,255,0.1)",
            borderRadius: 6, padding: "6px 12px",
            color: "rgba(255,255,255,0.4)", cursor: "pointer",
            fontSize: 9, fontFamily: "'Barlow Condensed',sans-serif",
            letterSpacing: 2, transition: "all 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(0,212,170,0.4)"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"}
        >
          ⛶ FULLSCREEN
        </button>
      </div>

      {/* ══ CONTRACT VALUE CARD ══════════════════════════ */}
      <div onClick={() => setModal({ type: "contract", title: "PORTFOLIO & CONTRACT DETAILS", wide: true })}
        style={{
          position: "relative", zIndex: 1, marginBottom: 10, cursor: "pointer",
          background: "linear-gradient(135deg, rgba(0,212,170,0.08), rgba(77,159,255,0.05))",
          border: "0.5px solid rgba(0,212,170,0.2)", borderRadius: 8, padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          boxShadow: "0 0 30px rgba(0,212,170,0.08)", transition: "border-color 0.2s"
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(0,212,170,0.4)"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(0,212,170,0.2)"}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, rgba(0,212,170,0.6), transparent)" }} />
        <CornerDeco pos="tl" /><CornerDeco pos="tr" />

        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div>
            <div style={{ fontSize: 9, color: "rgba(0,212,170,0.6)", letterSpacing: 3, fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 4 }}>TOTAL PORTFOLIO VALUE</div>
            <div style={{ fontSize: 36, fontWeight: 700, color: "#00D4AA", fontFamily: "'Barlow Condensed',sans-serif", lineHeight: 1, letterSpacing: -1 }}>
              {fmtSAR(totalContractValue)}
            </div>
          </div>
          <div style={{ width: 1, height: 40, background: "rgba(255,255,255,0.07)" }} />
          {projects.slice(0, 5).map((p, i) => (
            <div key={p.id} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.7)", fontFamily: "'Barlow Condensed',sans-serif" }}>
                {fmtSAR(p.total_value)}
              </div>
              <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 2 }}>
                {(p.short_name || p.name || "").split(" ").slice(0, 2).join(" ")}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 20 }}>
          {[
            { l: "Projects", v: projects.length, c: "#4D9FFF" },
            { l: "Workforce", v: totalWorkers, c: "#FFB84D" },
            { l: "On Track", v: projects.filter(p => getProjectProgress(p.id) >= 50).length, c: "#00D4AA" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.c, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.v}</div>
              <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>{s.l}</div>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", color: "rgba(0,212,170,0.5)", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2, gap: 4 }}>
            DETAILS →
          </div>
        </div>
      </div>

      {/* ══ KPI ROW ══════════════════════════════════════ */}
      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "repeat(9,1fr)", gap: 8, marginBottom: 10 }}>
        <KpiTile label="Active Projects" value={projects.length} sub={`${projects.filter(p => p.status === "Active").length} running`} />
        <KpiTile label="Overall Progress" value={overallProgress} sub="From tasks" color="#00D4AA" suffix="%" onClick={() => setModal({ type: "tasks", title: "TASK DETAILS" })} />
        <KpiTile label="Total Tasks" value={tasks.length} sub={`${doneTasks} completed`} onClick={() => setModal({ type: "tasks", title: "ALL TASKS" })} />
        <KpiTile label="In Progress" value={inProgressTasks} sub="Active" color="#4D9FFF" onClick={() => setModal({ type: "tasks", title: "IN PROGRESS" })} />
        <KpiTile label="Delayed" value={delayedTasks} sub="Need action" color={delayedTasks > 0 ? "#FF4D6D" : "#00D4AA"} alert={delayedTasks > 0} onClick={() => setModal({ type: "tasks", title: "DELAYED TASKS" })} />
        <KpiTile label="Materials" value={materials.length} sub={`${deliveredMats} delivered`} color="#FFB84D" onClick={() => setModal({ type: "materials", title: "MATERIALS" })} />
        <KpiTile label="Milestones" value={`${doneMilestones}/${milestones.length}`} sub={overdueMilestones > 0 ? `${overdueMilestones} overdue` : "On track"} color={overdueMilestones > 0 ? "#FF4D6D" : "#00D4AA"} alert={overdueMilestones > 0} onClick={() => setModal({ type: "milestones", title: "MILESTONES" })} />
        <KpiTile label="Budget Used" value={budgetPct} sub={fmtSAR(totalSpent)} color={budgetPct >= 90 ? "#FF4D6D" : budgetPct >= 70 ? "#FFB84D" : "#00D4AA"} alert={budgetPct >= 90} suffix="%" onClick={() => setModal({ type: "budget", title: "BUDGET" })} />
        <KpiTile label="Open Issues" value={challenges.length} sub="Challenges" color={challenges.length > 0 ? "#FFB84D" : "#00D4AA"} alert={challenges.length > 3} />
      </div>

      {/* ══ MAIN GRID ════════════════════════════════════ */}
      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "2fr 1.5fr 1.1fr", gap: 10, marginBottom: 10 }}>

        {/* ── LEFT ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Projects */}
          <Panel title="Project Schedule vs Baseline" badge={`${projects.length} Projects`}>
            {projects.map((p, idx) => {
              const prog = getProjectProgress(p.id);
              const projPhases = raw.phases.filter(ph => String(ph.project_id) === String(p.id));
              const baselineProgress = projPhases.length > 0 ? Math.round(projPhases.reduce((s, ph) => s + (ph.progress || 0), 0) / projPhases.length) : 0;
              const isDelayed = prog < baselineProgress - 10;
              const isDone = prog >= 100 || p.status === 'Completed';
              const isCancelled = p.status === 'Cancelled';
              const isOnHold = p.status === 'On Hold';
              const color = isCancelled ? "#94a3b8"
                : isOnHold ? "#FFB84D"
                  : isDone ? "#00D4AA"
                    : isDelayed ? "#FF4D6D"
                      : prog >= 70 ? "#FFB84D"
                        : "#4D9FFF";
              const projTasks = raw.tasks.filter(t => String(t.project_id) === String(p.id));
              const projDone = projTasks.filter(t => t.status === "Completed").length;
              const projDelayed = projTasks.filter(t => ["Delayed", "Overdue"].includes(t.status)).length;
              const projActive = projTasks.filter(t => t.status === "In Progress").length;
              return (
                <div key={p.id} onClick={() => setSelectedProject(selectedProject === String(p.id) ? "all" : String(p.id))}
                  style={{
                    padding: "12px 14px", borderRadius: 6, cursor: "pointer",
                    background: selectedProject === String(p.id) ? "rgba(0,212,170,0.06)" : "rgba(255,255,255,0.02)",
                    border: `0.5px solid ${selectedProject === String(p.id) ? "rgba(0,212,170,0.3)" : "rgba(255,255,255,0.05)"}`,
                    marginBottom: idx < projects.length - 1 ? 8 : 0, transition: "all 0.2s"
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 13, color: "#fff", fontWeight: 600, marginBottom: 3 }}>{p.name}</div>
                      <div style={{ display: "flex", gap: 10, fontSize: 9, color: "rgba(255,255,255,0.25)", fontFamily: "'Barlow Condensed',sans-serif" }}>
                        <span>{projTasks.length} TASKS</span>
                        <span style={{ color: "#00D4AA" }}>{projDone} DONE</span>
                        <span style={{ color: "#4D9FFF" }}>{projActive} ACTIVE</span>
                        {projDelayed > 0 && <span style={{ color: "#FF4D6D" }}>{projDelayed} DELAYED</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", marginLeft: 16 }}>
                      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>{prog}%</div>
                      <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>BL: {baselineProgress}%</div>
                    </div>
                  </div>
                  <SlimBar value={prog} baseline={baselineProgress} color={color} height={5} glow={!isDelayed} />
                </div>
              );
            })}
          </Panel>

          {/* Phases */}
          <Panel title="Phase Execution Status" badge={`${phases.length} Phases`}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 6, maxHeight: 220, overflowY: "auto" }}>
              {phases.map((ph, i) => {
                const prog = getPhaseProgress(ph);
                const isDone = prog >= 100 || ph.status === "Completed";
                const c = isDone ? "#00D4AA" : ph.is_critical ? "#FF4D6D" : "#4D9FFF";
                return (
                  <div key={i} style={{ background: "rgba(255,255,255,0.02)", border: `0.5px solid ${ph.is_critical ? "rgba(255,77,109,0.2)" : "rgba(255,255,255,0.05)"}`, borderRadius: 6, padding: "9px 10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "72%", fontFamily: "'Barlow Condensed',sans-serif" }}>{ph.phase_name || ph.name || ph.system_name}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {ph.is_critical && <span style={{ fontSize: 7, color: "#FF4D6D", fontFamily: "'Barlow Condensed',sans-serif" }}>CRIT</span>}
                        <span style={{ fontSize: 10, color: c, fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif" }}>{prog}%</span>
                      </div>
                    </div>
                    <SlimBar value={prog} color={c} glow={isDone} />
                    <div style={{ fontSize: 7, color: "rgba(255,255,255,0.15)", marginTop: 4, fontFamily: "'Barlow Condensed',sans-serif" }}>
                      {getProjectName(ph.project_id)}
                      {ph.baseline_end && ` · ${new Date(ph.baseline_end).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Budget */}
          <Panel title="Budget Overview" badge={`${budgetPct}% Used`}
            badgeColor={budgetPct >= 90 ? "#FF4D6D" : budgetPct >= 70 ? "#FFB84D" : "#00D4AA"}
            accent={budgetPct >= 90 ? "#FF4D6D" : "#00D4AA"}
            onClick={() => setModal({ type: "budget", title: "BUDGET DETAILS" })}>
            <div style={{ display: "flex", gap: 16, marginBottom: 12, alignItems: "center" }}>
              <RadialGauge size={80} value={budgetPct} color={budgetPct >= 90 ? "#FF4D6D" : budgetPct >= 70 ? "#FFB84D" : "#00D4AA"} label={`${budgetPct}%`} sublabel="used" thickness={8} />
              <div style={{ flex: 1 }}>
                {[
                  { l: "Allocated", v: fmtSAR(totalAllocated), c: "#4D9FFF" },
                  { l: "Spent", v: fmtSAR(totalSpent), c: budgetPct >= 90 ? "#FF4D6D" : "#FFB84D" },
                  { l: "Remaining", v: fmtSAR(totalAllocated - totalSpent), c: "#00D4AA" },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: "'Barlow Condensed',sans-serif" }}>{s.l}</span>
                    <span style={{ fontSize: 10, color: s.c, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{s.v}</span>
                  </div>
                ))}
              </div>
            </div>
            <BudgetChart categories={budgetCats.slice(0, 6)} expenses={budgetExp} />
          </Panel>
        </div>

        {/* ── MIDDLE ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
          {/* Tasks */}
          <Panel title="Task Execution Matrix" badge={`${pct(doneTasks, tasks.length)}% Complete`} badgeColor="#00D4AA"
            onClick={() => setModal({ type: "tasks", title: "TASK DETAILS" })}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
              <RadialGauge size={90} value={pct(doneTasks, tasks.length)} color="#00D4AA" label={`${pct(doneTasks, tasks.length)}%`} sublabel="complete" thickness={9} />
              <div style={{ flex: 1 }}>
                {[
                  { l: "Completed", v: doneTasks, c: "#00D4AA" },
                  { l: "In Progress", v: inProgressTasks, c: "#4D9FFF" },
                  { l: "Delayed", v: delayedTasks, c: "#FF4D6D" },
                  { l: "Not Started", v: notStarted, c: "rgba(255,255,255,0.2)" },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 5, height: 5, borderRadius: 1, background: s.c, boxShadow: i < 3 ? `0 0 5px ${s.c}` : "none" }} />
                    <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", flex: 1, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.l}</span>
                    <span style={{ fontSize: 12, color: "#fff", fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.15)", letterSpacing: 2, marginBottom: 8, fontFamily: "'Barlow Condensed',sans-serif" }}>PER PROJECT</div>
            {projects.map((p, i) => {
              const n = raw.tasks.filter(t => String(t.project_id) === String(p.id)).length;
              const done = raw.tasks.filter(t => String(t.project_id) === String(p.id) && t.status === "Completed").length;
              return (
                <div key={i} style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 8, color: "rgba(255,255,255,0.25)", fontFamily: "'Barlow Condensed',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{(p.short_name || p.name || "").split(" ").slice(0, 3).join(" ")}</span>
                    <span style={{ fontSize: 9, color: "#4D9FFF", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{done}/{n}</span>
                  </div>
                  <SlimBar value={pct(done, n)} color="#4D9FFF" height={3} />
                </div>
              );
            })}
          </Panel>

          {/* Milestones */}
          <Panel title="Milestone Tracker"
            badge={`${doneMilestones}/${milestones.length} Done`}
            badgeColor={overdueMilestones > 0 ? "#FF4D6D" : "#00D4AA"}
            accent={overdueMilestones > 0 ? "#FF4D6D" : "#00D4AA"}
            onClick={() => setModal({ type: "milestones", title: "ALL MILESTONES" })}>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              {[
                { l: "Done", v: doneMilestones, c: "#00D4AA" },
                { l: "In Progress", v: inProgressMiles, c: "#4D9FFF" },
                { l: "Overdue", v: overdueMilestones, c: "#FF4D6D" },
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, background: `${s.c}08`, border: `0.5px solid ${s.c}20`, borderRadius: 6, padding: "8px 10px", textAlign: "center" }}>
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 22, fontWeight: 700, color: s.c }}>{s.v}</div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>{s.l}</div>
                </div>
              ))}
            </div>
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {milestones.slice(0, 10).map((m, i) => {
                const isDone = m.status === "Completed";
                const daysLeft = m.planned_date ? Math.ceil((new Date(m.planned_date) - new Date()) / 86400000) : null;
                const isOverdue = !isDone && daysLeft !== null && daysLeft <= 0;
                const c = isDone ? "#00D4AA" : isOverdue ? "#FF4D6D" : daysLeft !== null && daysLeft <= 7 ? "#FFB84D" : "#4D9FFF";
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", border: `1.5px solid ${c}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isDone && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#00D4AA" }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, color: isDone ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: isDone ? "line-through" : "none" }}>{m.title || m.name}</div>
                      <div style={{ fontSize: 7, color: "rgba(255,255,255,0.15)", fontFamily: "'Barlow Condensed',sans-serif" }}>{getProjectName(m.project_id)}</div>
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, color: c, fontFamily: "'Barlow Condensed',sans-serif", flexShrink: 0 }}>
                      {isDone ? "DONE" : daysLeft !== null ? (daysLeft <= 0 ? "OVERDUE" : `${daysLeft}D`) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Live Activity */}
          <Panel title="Live Site Activity" badge="LIVE" badgeColor="#FF4D6D" accent="#FF4D6D"
            style={{ flex: 1 }}>
            <LiveActivityFeed activities={activities} projects={projects} />
          </Panel>
        </div>

        {/* ── RIGHT ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Portfolio */}
          <Panel title="Portfolio Overview" onClick={() => setModal({ type: "tasks", title: "PORTFOLIO" })}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <RadialGauge size={100} value={overallProgress} color={overallProgress >= 75 ? "#00D4AA" : overallProgress >= 50 ? "#FFB84D" : "#FF4D6D"} label={`${overallProgress}%`} sublabel="overall" thickness={10} />
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 7 }}>
                {projects.map((p, i) => {
                  const prog = getProjectProgress(p.id);
                  const c = p.status === 'Completed' ? "#00D4AA"
                    : p.status === 'Cancelled' ? "#94a3b8"
                      : prog >= 75 ? "#00D4AA"
                        : prog >= 50 ? "#FFB84D"
                          : "#4D9FFF";
                  return (
                    <div key={i}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "74%", fontFamily: "'Barlow Condensed',sans-serif" }}>{(p.short_name || p.name || "").split(" ").slice(0, 3).join(" ")}</span>
                        <span style={{ fontSize: 9, color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{prog}%</span>
                      </div>
                      <SlimBar value={prog} color={c} height={4} glow />
                    </div>
                  );
                })}
              </div>
            </div>
          </Panel>

          {/* Materials */}
          <Panel title="Materials Supply" badge={`${pendingMats} Pending`} badgeColor="#FFB84D"
            onClick={() => setModal({ type: "materials", title: "MATERIALS" })}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <RadialGauge size={65} value={pct(deliveredMats, materials.length)} color="#00D4AA" label={`${pct(deliveredMats, materials.length)}%`} sublabel="del." thickness={7} />
              <div style={{ flex: 1 }}>
                {[
                  { l: "Delivered", v: deliveredMats, c: "#00D4AA" },
                  { l: "Ordered", v: orderedMats, c: "#4D9FFF" },
                  { l: "Pending", v: pendingMats, c: "#FF4D6D" },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: s.c }} />
                    <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", flex: 1, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.l}</span>
                    <span style={{ fontSize: 11, color: "#fff", fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          {/* Team */}
          <Panel title="Team Roster" badge={`${raw.teamMembers.length} Members`}
            onClick={() => setModal({ type: "team", title: "TEAM OVERVIEW" })}>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {raw.teamMembers.slice(0, 12).map((member, i) => {
                const initials = (member.name || "??").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                const colors = ["#00D4AA", "#4D9FFF", "#FFB84D", "#FF4D6D", "#A78BFA"];
                const c = colors[i % 5];
                const memberTasks = raw.tasks.filter(t => t.assigned_to === member.name);
                const done = memberTasks.filter(t => t.status === "Completed").length;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                    {member.avatar
                      ? <img src={member.avatar} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", border: `0.5px solid ${c}40` }} />
                      : <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${c}15`, border: `0.5px solid ${c}40`, color: c, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0, fontFamily: "'Barlow Condensed',sans-serif" }}>{initials}</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.name}</div>
                      <div style={{ fontSize: 7, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif" }}>{member.position || "—"}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: "#fff", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>{memberTasks.length}</div>
                      <div style={{ fontSize: 7, color: "#00D4AA", fontFamily: "'Barlow Condensed',sans-serif" }}>{done} done</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Issues */}
          <Panel title="Issues & Challenges" badge={`${challenges.length} Open`}
            badgeColor={challenges.length > 0 ? "#FF4D6D" : "#00D4AA"}
            accent={challenges.length > 0 ? "#FF4D6D" : "#00D4AA"}>
            {challenges.length === 0
              ? <div style={{ textAlign: "center", padding: 16, color: "#00D4AA", fontSize: 10, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2 }}>NO OPEN ISSUES</div>
              : <div style={{ maxHeight: 160, overflowY: "auto" }}>
                {challenges.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#FF4D6D", marginTop: 4, flexShrink: 0, boxShadow: "0 0 6px #FF4D6D" }} />
                    <div>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>{c.description || c.challenge || c.title || "—"}</div>
                      <div style={{ fontSize: 7, color: "rgba(255,255,255,0.15)", fontFamily: "'Barlow Condensed',sans-serif" }}>{getProjectName(c.project_id)}</div>
                    </div>
                  </div>
                ))}
              </div>}
          </Panel>
          {/* Tickets Summary */}
          <Panel title="Support Tickets" badge="LIVE" badgeColor="#FF4D6D"
            onClick={() => window.open('/dashboard/itsm/tickets', '_blank')}
            style={{ flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%", justifyContent: "space-between" }}>
              {[
                { l: "Pending Review", v: raw.tickets?.filter(t => t.status === 'pending_approval').length || 0, c: "#FFB84D" },
                { l: "In Progress", v: raw.tickets?.filter(t => t.status === 'in_progress').length || 0, c: "#4D9FFF" },
                { l: "Open Total", v: raw.tickets?.filter(t => t.status !== 'closed').length || 0, c: "#FF4D6D" },
                { l: "Closed", v: raw.tickets?.filter(t => t.status === 'closed').length || 0, c: "#00D4AA" },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.c, boxShadow: `0 0 6px ${s.c}`, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", flex: 1, fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 1 }}>{s.l}</span>
                  <span style={{ fontSize: 28, color: "#fff", fontWeight: 700, fontFamily: "'Barlow Condensed',sans-serif" }}>{s.v}</span>
                </div>
              ))}
              <div style={{ paddingTop: 12 }}>
                <SlimBar value={raw.tickets?.length > 0 ? pct(raw.tickets?.filter(t => t.status === 'closed').length, raw.tickets?.length) : 0} color="#00D4AA" height={4} glow />
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", fontFamily: "'Barlow Condensed',sans-serif", marginTop: 6, textAlign: "right" }}>
                  {pct(raw.tickets?.filter(t => t.status === 'closed').length || 0, raw.tickets?.length || 1)}% resolution rate
                </div>
              </div>
            </div>
          </Panel>

        </div>
      </div>

      {/* ══ BOTTOM ROW — Gantt + Map ══════════════════════ */}
      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 10 }}>

        {/* Gantt Timeline */}
        <Panel title="Project Timeline" badge="Schedule" badgeColor="#4D9FFF" accent="#4D9FFF">
          <GanttChart projects={projects} />
        </Panel>

        {/* Map */}
        <Panel title="Project Locations" badge={`${projects.filter(p => p.latitude && p.longitude).length} Sites`} noPad>
          <div style={{ height: 280 }}>
            <ProjectsMap projects={projects} />
          </div>
        </Panel>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;400;500;600;700&family=Barlow:wght@400;500&display=swap');
        @keyframes ripple{0%{transform:scale(1);opacity:0.4}100%{transform:scale(3);opacity:0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}
        @keyframes countdown{from{width:100%}to{width:0%}}
        ::-webkit-scrollbar{width:2px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(0,212,170,0.2);border-radius:2px}
        *{box-sizing:border-box}
      `}</style>
    </div>
  );
}

export default function TVDashboard() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("tv_unlocked") === "true");
  const handleUnlock = () => { sessionStorage.setItem("tv_unlocked", "true"); setUnlocked(true); };
  if (!unlocked) return <PinScreen onUnlock={handleUnlock} />;
  return <CommandCenter />;
}